import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Reaction from '../../model/Reaction'
import { dataSource } from '../../service/domain/_service.config'
import { useUser } from '../user/useUser'
import { optimisticUpdate } from '../util/optimisticUpdate'

const REACTIONS_QUERY_KEY = (messageId: string) => ['reactions', messageId]

export function useReactions(messageId: string) {
  const { data: user } = useUser()
  const queryClient = useQueryClient()

  const { data: reactions = [] } = useQuery({
    queryKey: REACTIONS_QUERY_KEY(messageId),
    queryFn: () => dataSource.getReactions(messageId),
    enabled: !!messageId,
  })

  const addOrUpdateReactionMutation = useMutation({
    mutationFn: async (emoji: string) => {
      if (!user) throw new Error('User must be logged in to react')

      const currentReactions =
        (queryClient.getQueryData(REACTIONS_QUERY_KEY(messageId)) as Reaction[] | undefined) ?? []
      const existingReaction = currentReactions.find((reaction) => reaction.userId === user.id)

      if (existingReaction?.id != null) {
        const updatedReaction = new Reaction({
          ...existingReaction,
          emoji,
        })
        await dataSource.updateReaction(updatedReaction)
        return updatedReaction
      }

      return dataSource.saveReaction(
        new Reaction({
          messageId,
          userId: user.id,
          emoji,
        }),
      )
    },
    ...optimisticUpdate<Reaction[], string>(
      queryClient,
      REACTIONS_QUERY_KEY(messageId),
      (current, emoji) => {
        if (!user) return current
        const existingReaction = current.find((reaction) => reaction.userId === user.id)
        return existingReaction
          ? current.map((reaction) =>
              reaction.userId === user.id ? new Reaction({ ...reaction, emoji }) : reaction,
            )
          : [...current, new Reaction({ messageId, userId: user.id, emoji })]
      },
    ),
  })

  const removeReactionMutation = useMutation({
    mutationFn: async (reactionId: number) => {
      await dataSource.deleteReaction(reactionId)
    },
    ...optimisticUpdate<Reaction[], number>(
      queryClient,
      REACTIONS_QUERY_KEY(messageId),
      (current, reactionId) => current.filter((reaction) => reaction.id !== reactionId),
    ),
  })

  return {
    reactions,
    addOrUpdateReaction: addOrUpdateReactionMutation.mutateAsync,
    removeReaction: removeReactionMutation.mutateAsync,
    isLoading: addOrUpdateReactionMutation.isPending || removeReactionMutation.isPending,
  }
}

export default useReactions
